"use client"

import { ExclamationCircleIcon } from "@heroicons/react/16/solid"
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/20/solid"
import { useSystemStatusViewed } from "@src/hooks/useSystemStatusViewed"
import { useSystemStatus } from "@src/providers/SystemStatusProvider"
import { APP_NETWORK_OUTAGE_NOTIFICATION } from "@src/utils/environment"
import clsx from "clsx"
import { usePathname } from "next/navigation"
import { useState } from "react"
import { AdaptiveOverlay } from "./AdaptiveOverlay"
import Button from "./Button"
import Drawer from "./Drawer"

const SYSTEM_STATUS_URL = "https://status.near-intents.org/posts/dashboard"

function SystemStatusMobile() {
  const [open, setOpen] = useState(false)
  const pathname = usePathname()
  const statuses = useSystemStatus()
  const { viewed, markViewed } = useSystemStatusViewed()

  const shouldShowNotification = [
    "/home",
    "/receive",
    "/swap",
    "/send",
    "/send/confidential",
  ].includes(pathname)

  if (!shouldShowNotification) {
    return null
  }

  const statusType = statuses[0]?.status ?? null
  const externalMessages = [
    ...new Set(statuses.map((status) => status.message)),
  ]
  const messages = APP_NETWORK_OUTAGE_NOTIFICATION
    ? [...externalMessages, APP_NETWORK_OUTAGE_NOTIFICATION]
    : externalMessages

  if (messages.length === 0) return null

  const label =
    statusType === "incident"
      ? "Incident detected"
      : statusType === "maintenance"
        ? "Maintenance in progress"
        : "Service notice"

  const colorClasses =
    statusType === "incident"
      ? "bg-red-100 text-red-600 dark:bg-red-500/15 dark:text-red-300 outline-transparent"
      : "bg-yellow-100 text-yellow-600 dark:bg-yellow-500/15 dark:text-yellow-300 dark:outline-transparent outline-yellow-500/7"

  return (
    <>
      <Button
        variant={statusType === "incident" ? "destructive-soft" : "warning"}
        size="lg"
        className="size-10! overflow-hidden xl:hidden"
        onClick={() => {
          markViewed()
          setOpen(true)
        }}
      >
        <span className="sr-only">{label}</span>
        {!viewed && (
          <>
            <span
              className={clsx(
                "pointer-events-none absolute inset-0 m-auto size-5 animate-ping-slow select-none rounded-full",
                statusType === "incident" ? "bg-red-500" : "bg-yellow-500"
              )}
            />
            <span className="absolute flex size-5 items-center justify-center p-1">
              <span
                className={clsx(
                  "size-full rounded-full",
                  statusType === "incident"
                    ? "bg-red-100 dark:bg-red-900"
                    : "bg-yellow-100 dark:bg-yellow-800"
                )}
              />
            </span>
          </>
        )}
        <ExclamationCircleIcon className="relative size-5" />
      </Button>

      <AdaptiveOverlay
        open={open}
        onClose={() => setOpen(false)}
        desktop={null}
        mobile={{
          type: "drawer",
          props: {
            header: (
              <div>
                <div
                  className={clsx(
                    "flex size-12 items-center justify-center rounded-full",
                    colorClasses
                  )}
                >
                  <ExclamationCircleIcon className="size-5 shrink-0" />
                </div>
                <Drawer.Title className="mt-4 font-semibold text-2xl/7 text-gray-900 tracking-tight dark:text-white">
                  {label}
                </Drawer.Title>
              </div>
            ),
          },
        }}
      >
        <div className="font-medium text-base/5 text-gray-900 dark:text-white">
          {messages.length === 1 ? (
            <p>{messages[0]}</p>
          ) : (
            <ul className="list-disc space-y-3 pl-3.5">
              {messages.map((message) => (
                <li key={message}>{message}</li>
              ))}
            </ul>
          )}
        </div>

        <Button
          size="xl"
          variant="secondary"
          className="mt-6"
          fullWidth
          href={SYSTEM_STATUS_URL}
          target="_blank"
          rel="noreferrer noopener"
        >
          View status
          <ArrowTopRightOnSquareIcon className="size-5 shrink-0" />
        </Button>
      </AdaptiveOverlay>
    </>
  )
}

export default SystemStatusMobile
